import { isSupabaseConfigured, supabase } from "../lib/supabase";
import { LOW_STOCK_THRESHOLD, loadCatalog, saveCatalog } from "./catalog";

const MOVEMENTS_STORAGE_KEY = "almoune_inventory_movements";
const LOTS_STORAGE_KEY = "almoune_inventory_lots";

const movementLabels = {
  in: "Entree",
  out: "Sortie",
  reserve: "Reservation",
  release: "Liberation",
  adjustment: "Ajustement",
  waste: "Perte",
  sale: "Vente",
};

const referenceLabels = {
  "": "Manuel",
  cart: "Panier",
  order: "Commande",
  manual: "Manuel",
  purchase: "Achat",
  waste: "Perte",
};

const ensureMovementArray = (value) =>
  Array.isArray(value) ? value : [];

const readMovementCache = () => {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(MOVEMENTS_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    return ensureMovementArray(JSON.parse(raw));
  } catch (error) {
    return [];
  }
};

const writeMovementCache = (movements) => {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(
      MOVEMENTS_STORAGE_KEY,
      JSON.stringify(ensureMovementArray(movements))
    );
  }
};

const readLotCache = () => {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(LOTS_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    return ensureMovementArray(JSON.parse(raw));
  } catch (error) {
    return [];
  }
};

const writeLotCache = (lots) => {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(LOTS_STORAGE_KEY, JSON.stringify(ensureMovementArray(lots)));
  }
};

const normalizeMovement = (movement, productMap = new Map()) => {
  const productId = movement.product_id || movement.productId || "";
  const product = productMap.get(productId) || {};
  const createdAt = movement.created_at || movement.createdAt || "";

  return {
    id: movement.id,
    lotId: movement.lot_id || movement.lotId || null,
    lotCode: movement.lot_code || movement.lotCode || "",
    productId,
    productName: product.name || movement.product_name || "Produit",
    movementType: movement.movement_type || movement.movementType || "adjustment",
    movementLabel:
      movementLabels[movement.movement_type || movement.movementType] ||
      "Mouvement",
    quantity: Number(movement.quantity || 0),
    unitType: movement.unit_type || movement.unitType || product.unitType || "piece",
    reason: movement.reason || "",
    referenceType: movement.reference_type || movement.referenceType || "",
    referenceLabel:
      referenceLabels[movement.reference_type || movement.referenceType || ""] ||
      "Reference",
    referenceId:
      movement.reference_id?.toString?.() ||
      movement.referenceId?.toString?.() ||
      "",
    createdAt,
    createdAtLabel: createdAt
      ? new Date(createdAt).toLocaleString("fr-FR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "",
  };
};

const appendCachedMovement = async (movement) => {
  const catalog = await loadCatalog();
  const productMap = new Map(catalog.map((item) => [item.id, item]));
  const cached = readMovementCache();
  const next = [
    normalizeMovement(
      {
        ...movement,
        id: movement.id || `mov-${Date.now()}`,
        created_at: movement.created_at || new Date().toISOString(),
      },
      productMap
    ),
    ...cached,
  ].slice(0, 200);

  writeMovementCache(next);
  return next;
};

const getMainInventoryRow = async (productId) => {
  const { data, error } = await supabase
    .from("inventory")
    .select("id, product_id, variant_id, stock_quantity, reserved_stock, location_label")
    .eq("product_id", productId)
    .eq("location_label", "Main stock")
    .is("variant_id", null)
    .maybeSingle();

  if (error) {
    throw new Error("Impossible de charger le stock de ce produit.");
  }

  if (data) {
    return data;
  }

  const { data: insertedRow, error: insertError } = await supabase
    .from("inventory")
    .insert({
      product_id: productId,
      variant_id: null,
      stock_quantity: 0,
      reserved_stock: 0,
      reorder_threshold: LOW_STOCK_THRESHOLD,
      location_label: "Main stock",
    })
    .select("id, product_id, variant_id, stock_quantity, reserved_stock, location_label")
    .single();

  if (insertError || !insertedRow) {
    throw new Error("Impossible d'initialiser le stock de ce produit.");
  }

  return insertedRow;
};

const normalizeLot = (lot, productMap = new Map(), inventoryMap = new Map()) => {
  const inventoryId = lot.inventory_id || lot.inventoryId || "";
  const inventory = inventoryMap.get(inventoryId) || {};
  const product = productMap.get(inventory.product_id || lot.product_id || "") || {};
  const expiryDate = lot.expiry_date || lot.expiryDate || null;
  const quantity = Number(lot.quantity || 0);
  const diffDays = expiryDate
    ? Math.ceil((new Date(expiryDate).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / (1000 * 60 * 60 * 24))
    : null;

  return {
    id: lot.id || `lot-${Date.now()}`,
    inventoryId,
    productId: inventory.product_id || lot.product_id || "",
    productName: product.name || lot.product_name || "Produit",
    lotCode: lot.lot_code || lot.lotCode || "",
    quantity,
    unitType: lot.unit_type || lot.unitType || product.unitType || "piece",
    parentLotId: lot.parent_lot_id || lot.parentLotId || null,
    supplierName: lot.supplier_name || lot.supplierName || "",
    receivedDate: lot.received_date || lot.receivedDate || null,
    expiryDate,
    notes: lot.notes || "",
    purchasePriceHt: Number(lot.purchase_price_ht ?? lot.purchasePriceHt ?? 0),
    supplierInvoiceRef: lot.supplier_invoice_ref || lot.supplierInvoiceRef || "",
    receivedAt: lot.received_at || lot.receivedAt || "",
    isExpired: diffDays !== null && diffDays < 0,
    expiresSoon: diffDays !== null && diffDays >= 0 && diffDays <= 7,
    daysUntilExpiry: diffDays,
  };
};

export const recordInventoryMovement = async ({
  inventoryId,
  lotId = null,
  productId,
  movementType,
  quantity,
  unitType = "piece",
  referenceType = "",
  referenceId = "",
  reason = "",
}) => {
  const payload = {
    inventory_id: inventoryId,
    lot_id: lotId,
    product_id: productId,
    movement_type: movementType,
    quantity: Number(quantity || 0),
    unit_type: unitType,
    reference_type: referenceType,
    reference_id: referenceId ? String(referenceId) : null,
    reason,
  };

  if (!isSupabaseConfigured || !supabase) {
    await appendCachedMovement(payload);
    return true;
  }

  const { error } = await supabase.from("inventory_movements").insert(payload);

  if (error) {
    await appendCachedMovement({
      ...payload,
      created_at: new Date().toISOString(),
    });
    return false;
  }

  return true;
};

export const adjustInventoryStock = async (
  productId,
  delta,
  reason = "Ajustement manuel"
) => {
  const numericDelta = Number(delta || 0);

  if (!numericDelta) {
    return loadCatalog();
  }

  if (!isSupabaseConfigured || !supabase) {
    const catalog = await loadCatalog();
    const updated = await saveCatalog(
      catalog.map((product) =>
        product.id === productId
          ? { ...product, stock: Math.max(0, Number(product.stock || 0) + numericDelta) }
          : product
      )
    );
    await appendCachedMovement({
      product_id: productId,
      movement_type: numericDelta > 0 ? "in" : "out",
      quantity: Math.abs(numericDelta),
      unit_type: "piece",
      reference_type: "manual",
      reason,
    });
    return updated;
  }

  const inventoryRow = await getMainInventoryRow(productId);
  const nextStock = Number(inventoryRow.stock_quantity || 0) + numericDelta;

  if (nextStock < 0) {
    throw new Error("Le stock ne peut pas devenir negatif.");
  }

  const { error } = await supabase
    .from("inventory")
    .update({ stock_quantity: nextStock })
    .eq("id", inventoryRow.id);

  if (error) {
    throw error;
  }

  await recordInventoryMovement({
    inventoryId: inventoryRow.id,
    productId,
    movementType: numericDelta > 0 ? "in" : "out",
    quantity: Math.abs(numericDelta),
    referenceType: "manual",
    reason,
  });

  return loadCatalog();
};

export const fulfillInventorySale = async ({
  productId,
  quantity,
  unitType = "piece",
  referenceId = "",
  reason = "Vente caisse",
}) => {
  const requestedQuantity = Number(quantity || 0);

  if (!productId || requestedQuantity <= 0) {
    throw new Error("Quantite de vente invalide.");
  }

  if (!isSupabaseConfigured || !supabase) {
    const catalog = await loadCatalog();
    const product = catalog.find((item) => item.id === productId);

    if (!product) {
      throw new Error("Produit introuvable pour cette vente.");
    }

    const nextStock = Number(product.stock || 0) - requestedQuantity;
    if (nextStock < 0) {
      throw new Error(`Stock insuffisant pour ${product.name}.`);
    }

    await saveCatalog(
      catalog.map((item) =>
        item.id === productId ? { ...item, stock: Number(nextStock.toFixed(3)) } : item
      )
    );

    await appendCachedMovement({
      product_id: productId,
      movement_type: "sale",
      quantity: requestedQuantity,
      unit_type: unitType,
      reference_type: "order",
      reference_id: referenceId,
      reason,
    });

    return true;
  }

  const inventoryRow = await getMainInventoryRow(productId);
  const productLots = await loadInventoryLots(productId);
  const hasTrackedLots = productLots.some((lot) => Number(lot.quantity || 0) > 0);
  const nextStock = Number(inventoryRow.stock_quantity || 0) - requestedQuantity;

  if (nextStock < 0) {
    throw new Error("Stock insuffisant pour couvrir cette vente.");
  }

  const { error: inventoryError } = await supabase
    .from("inventory")
    .update({ stock_quantity: Number(nextStock.toFixed(3)) })
    .eq("id", inventoryRow.id);

  if (inventoryError) {
    throw inventoryError;
  }

  if (hasTrackedLots) {
    await consumeInventoryLots({
      productId,
      quantity: requestedQuantity,
      unitType,
      referenceId,
      reason,
    });
  } else {
    await recordInventoryMovement({
      inventoryId: inventoryRow.id,
      productId,
      movementType: "sale",
      quantity: requestedQuantity,
      unitType,
      referenceType: "order",
      referenceId,
      reason,
    });
  }

  return true;
};

export const setInventoryStock = async (
  productId,
  nextQuantity,
  reason = "Correction manuelle"
) => {
  const target = Number(nextQuantity);
  if (!Number.isFinite(target) || target < 0) {
    throw new Error("Quantite invalide.");
  }

  if (!isSupabaseConfigured || !supabase) {
    const catalog = await loadCatalog();
    const current = catalog.find((product) => product.id === productId);
    const delta = target - Number(current?.stock || 0);
    const updated = await saveCatalog(
      catalog.map((product) =>
        product.id === productId ? { ...product, stock: target } : product
      )
    );
    if (delta !== 0) {
      await appendCachedMovement({
        product_id: productId,
        movement_type: "adjustment",
        quantity: Math.abs(delta),
        unit_type: current?.unitType || "piece",
        reference_type: "manual",
        reason,
      });
    }
    return updated;
  }

  const inventoryRow = await getMainInventoryRow(productId);
  const delta = target - Number(inventoryRow.stock_quantity || 0);

  const { error } = await supabase
    .from("inventory")
    .update({ stock_quantity: target })
    .eq("id", inventoryRow.id);

  if (error) {
    throw error;
  }

  if (delta !== 0) {
    await recordInventoryMovement({
      inventoryId: inventoryRow.id,
      productId,
      movementType: "adjustment",
      quantity: Math.abs(delta),
      referenceType: "manual",
      reason,
    });
  }

  return loadCatalog();
};

export const loadInventoryMovements = async (limit = 30, { dateFrom, dateTo } = {}) => {
  const catalog = await loadCatalog();
  const productMap = new Map(catalog.map((item) => [item.id, item]));
  const cached = readMovementCache();

  if (!isSupabaseConfigured || !supabase) {
    let result = cached;
    if (dateFrom) result = result.filter((m) => (m.createdAt || "").slice(0, 10) >= dateFrom);
    if (dateTo) result = result.filter((m) => (m.createdAt || "").slice(0, 10) <= dateTo);
    return result.slice(0, limit);
  }

  const { data, error } = await supabase
    .from("inventory_movements")
    .select(
      "id, lot_id, product_id, movement_type, quantity, unit_type, reason, reference_type, reference_id, created_at"
    )
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error || !data) {
    let result = cached;
    if (dateFrom) result = result.filter((m) => (m.createdAt || "").slice(0, 10) >= dateFrom);
    if (dateTo) result = result.filter((m) => (m.createdAt || "").slice(0, 10) <= dateTo);
    return result.slice(0, limit);
  }

  const normalized = data.map((item) => normalizeMovement(item, productMap));
  writeMovementCache(normalized);
  let result = normalized;
  if (dateFrom) result = result.filter((m) => (m.createdAt || "").slice(0, 10) >= dateFrom);
  if (dateTo) result = result.filter((m) => (m.createdAt || "").slice(0, 10) <= dateTo);
  return result;
};

export const loadOrderLotConsumption = async (orderId = "") => {
  if (!orderId) {
    return {};
  }

  if (!isSupabaseConfigured || !supabase) {
    const cachedMovements = readMovementCache().filter(
      (movement) =>
        (movement.reference_id || movement.referenceId) === orderId &&
        (movement.movement_type || movement.movementType) === "sale" &&
        (movement.lot_id || movement.lotId)
    );

    return cachedMovements.reduce((acc, movement) => {
      const productId = movement.product_id || movement.productId || "";
      if (!acc[productId]) {
        acc[productId] = [];
      }
      acc[productId].push({
        lotId: movement.lot_id || movement.lotId,
        lotCode: movement.lot_code || movement.lotCode || "Lot",
        quantity: Number(movement.quantity || 0),
        unitType: movement.unit_type || movement.unitType || "piece",
      });
      return acc;
    }, {});
  }

  const { data: movements, error: movementError } = await supabase
    .from("inventory_movements")
    .select("id, lot_id, product_id, quantity, unit_type")
    .eq("reference_type", "order")
    .eq("reference_id", orderId)
    .eq("movement_type", "sale")
    .not("lot_id", "is", null);

  if (movementError || !movements || !movements.length) {
    return {};
  }

  const lotIds = [...new Set(movements.map((movement) => movement.lot_id).filter(Boolean))];
  const { data: lots } = await supabase
    .from("inventory_lots")
    .select("id, lot_code")
    .in("id", lotIds);

  const lotCodeMap = new Map((lots || []).map((lot) => [lot.id, lot.lot_code || "Lot"]));

  return movements.reduce((acc, movement) => {
    const productId = movement.product_id || "";
    if (!acc[productId]) {
      acc[productId] = [];
    }
    acc[productId].push({
      lotId: movement.lot_id,
      lotCode: lotCodeMap.get(movement.lot_id) || "Lot",
      quantity: Number(movement.quantity || 0),
      unitType: movement.unit_type || "piece",
    });
    return acc;
  }, {});
};

export const loadInventoryLots = async (productId = "") => {
  const catalog = await loadCatalog();
  const productMap = new Map(catalog.map((item) => [item.id, item]));
  const cachedLots = readLotCache();

  if (!isSupabaseConfigured || !supabase) {
    const inventoryMap = new Map(
      catalog.map((product) => [
        `cached-inventory-${product.id}`,
        { id: `cached-inventory-${product.id}`, product_id: product.id },
      ])
    );
    const normalizedCached = cachedLots
      .map((lot) => normalizeLot(lot, productMap, inventoryMap))
      .filter((lot) => !productId || lot.productId === productId);
    return normalizedCached;
  }

  const { data: inventories } = await supabase
    .from("inventory")
    .select("id, product_id")
    .is("variant_id", null);

  const inventoryMap = new Map((inventories || []).map((row) => [row.id, row]));
  const inventoryIds = (inventories || [])
    .filter((row) => !productId || row.product_id === productId)
    .map((row) => row.id);

  if (!inventoryIds.length) {
    return [];
  }

  const { data, error } = await supabase
    .from("inventory_lots")
    .select("id, inventory_id, lot_code, quantity, expiry_date, received_at, created_at")
    .in("inventory_id", inventoryIds)
    .order("expiry_date", { ascending: true, nullsFirst: false });

  if (error || !data) {
    return cachedLots
      .map((lot) => normalizeLot(lot, productMap, inventoryMap))
      .filter((lot) => !productId || lot.productId === productId);
  }

  const normalized = data.map((lot) => normalizeLot(lot, productMap, inventoryMap));
  writeLotCache(normalized);
  return normalized;
};

export const saveInventoryLot = async (productId, lot) => {
  const catalog = await loadCatalog();
  const product = catalog.find((item) => item.id === productId);

  if (!product) {
    throw new Error("Produit introuvable pour ce lot.");
  }

  const normalizedLot = {
    id: lot.id || `lot-${Date.now()}`,
    lot_code: lot.lotCode?.trim() || lot.lot_code?.trim() || "",
    quantity: Number(lot.quantity || 0),
    unit_type: lot.unitType || lot.unit_type || "piece",
    parent_lot_id: lot.parentLotId || lot.parent_lot_id || null,
    supplier_name: lot.supplierName || lot.supplier_name || "",
    received_date: lot.receivedDate || lot.received_date || null,
    expiry_date: lot.expiryDate || lot.expiry_date || null,
    notes: lot.notes || "",
    received_at: lot.receivedAt || lot.received_at || new Date().toISOString(),
  };

  if (!isSupabaseConfigured || !supabase) {
    const cachedLots = readLotCache();
    const cacheInventoryId = `cached-inventory-${productId}`;
    const nextLots = cachedLots.some((item) => item.id === normalizedLot.id)
      ? cachedLots.map((item) =>
          item.id === normalizedLot.id
            ? { ...normalizedLot, inventory_id: cacheInventoryId, product_id: productId }
            : item
        )
      : [{ ...normalizedLot, inventory_id: cacheInventoryId, product_id: productId }, ...cachedLots];
    writeLotCache(nextLots);
    return normalizeLot(
      { ...normalizedLot, inventory_id: cacheInventoryId, product_id: productId },
      new Map([[product.id, product]]),
      new Map([[cacheInventoryId, { id: cacheInventoryId, product_id: productId }]])
    );
  }

  const inventoryRow = await getMainInventoryRow(productId);
  const payload = {
    id: lot.id || undefined,
    inventory_id: inventoryRow.id,
    lot_code: normalizedLot.lot_code,
    quantity: normalizedLot.quantity,
    unit_type: normalizedLot.unit_type,
    parent_lot_id: normalizedLot.parent_lot_id,
    supplier_name: normalizedLot.supplier_name,
    received_date: normalizedLot.received_date,
    expiry_date: normalizedLot.expiry_date,
    notes: normalizedLot.notes,
    received_at: normalizedLot.received_at,
  };

  const { data, error } = await supabase
    .from("inventory_lots")
    .upsert([payload], { onConflict: "id" })
    .select("id, inventory_id, lot_code, quantity, expiry_date, received_at")
    .single();

  if (error) {
    throw error;
  }

  return normalizeLot(
    data,
    new Map([[product.id, product]]),
    new Map([[inventoryRow.id, inventoryRow]])
  );
};

export const breakdownLot = async (parentLotId, childUnits, childUnitType = "piece") => {
  const parentLots = await loadInventoryLots("");
  const parentLot = parentLots.find((l) => l.id === parentLotId);
  if (!parentLot) throw new Error("Lot parent introuvable.");

  const totalChildQuantity = childUnits.reduce((sum, u) => sum + Number(u.quantity || 0), 0);
  if (totalChildQuantity > parentLot.quantity) {
    throw new Error(`Quantite totale des units (${totalChildQuantity}) depasse le lot parent (${parentLot.quantity}).`);
  }

  const createdChildren = [];
  for (const unit of childUnits) {
    const childLot = {
      lotCode: unit.lotCode || `${parentLot.lotCode}-F${createdChildren.length + 1}`,
      quantity: Number(unit.quantity || 0),
      unitType: childUnitType,
      parentLotId: parentLotId,
      supplierName: parentLot.supplierName,
      receivedDate: parentLot.receivedDate || new Date().toISOString().slice(0, 10),
      expiryDate: parentLot.expiryDate,
      notes: unit.notes || `Decoupe depuis ${parentLot.lotCode}`,
    };
    const saved = await saveInventoryLot(parentLot.productId, childLot);
    createdChildren.push(saved);
  }

  const remainingParent = Number((parentLot.quantity - totalChildQuantity).toFixed(3));
  await saveInventoryLot(parentLot.productId, {
    ...parentLot,
    quantity: remainingParent,
  });

  return createdChildren;
};

export const deleteInventoryLot = async (lotId) => {
  if (!isSupabaseConfigured || !supabase) {
    writeLotCache(readLotCache().filter((lot) => lot.id !== lotId));
    return true;
  }

  const { error } = await supabase.from("inventory_lots").delete().eq("id", lotId);
  if (error) {
    throw error;
  }
  return true;
};

export const getInventoryAlertStats = async () => {
  const catalog = await loadCatalog();
  const lots = await loadInventoryLots();
  const lowStock = catalog.filter(
    (product) => Number(product.stock || 0) <= LOW_STOCK_THRESHOLD
  ).length;
  const reservedUnits = catalog.reduce(
    (sum, product) => sum + Number(product.reservedStock || 0),
    0
  );
  const expiringSoon = lots.filter((lot) => lot.expiresSoon).length;
  const expiredLots = lots.filter((lot) => lot.isExpired).length;

  return { lowStock, reservedUnits, expiringSoon, expiredLots };
};

export const consumeInventoryLots = async ({
  productId,
  quantity,
  referenceId = "",
  reason = "",
  unitType = "piece",
}) => {
  const requestedQuantity = Number(quantity || 0);

  if (requestedQuantity <= 0) {
    return [];
  }

  if (!isSupabaseConfigured || !supabase) {
    const cachedLots = readLotCache();
    const productLots = cachedLots
      .filter((lot) => lot.product_id === productId || lot.productId === productId)
      .map((lot) => ({
        ...lot,
        quantity: Number(lot.quantity || 0),
        expiry_date: lot.expiry_date || lot.expiryDate || null,
        received_at: lot.received_at || lot.receivedAt || "",
      }))
      .filter((lot) => !lot.expiry_date || new Date(lot.expiry_date) >= new Date(new Date().toDateString()))
      .sort((a, b) => {
        const expiryA = a.expiry_date ? new Date(a.expiry_date).getTime() : Number.MAX_SAFE_INTEGER;
        const expiryB = b.expiry_date ? new Date(b.expiry_date).getTime() : Number.MAX_SAFE_INTEGER;
        if (expiryA !== expiryB) {
          return expiryA - expiryB;
        }
        return new Date(a.received_at || 0).getTime() - new Date(b.received_at || 0).getTime();
      });

    let remaining = requestedQuantity;
    const updates = [];
    const totalAvailable = productLots.reduce(
      (sum, lot) => sum + Number(lot.quantity || 0),
      0
    );

    if (totalAvailable < requestedQuantity) {
      throw new Error("Lots insuffisants ou expires pour couvrir cette vente.");
    }

    for (const lot of productLots) {
      if (remaining <= 0) {
        break;
      }
      const available = Number(lot.quantity || 0);
      if (available <= 0) {
        continue;
      }
      const consumed = Math.min(available, remaining);
      remaining = Number((remaining - consumed).toFixed(3));
      updates.push({ lotId: lot.id, quantity: consumed });
    }

    if (remaining > 0) {
      throw new Error("Lots insuffisants ou expires pour couvrir cette vente.");
    }

    const nextLots = cachedLots.map((lot) => {
      const update = updates.find((entry) => entry.lotId === lot.id);
      if (!update) {
        return lot;
      }
      return {
        ...lot,
        quantity: Number((Number(lot.quantity || 0) - update.quantity).toFixed(3)),
      };
    });
    writeLotCache(nextLots);
    return updates;
  }

  const inventoryRow = await getMainInventoryRow(productId);
  const today = new Date().toISOString().slice(0, 10);
  const { data: lotRows, error } = await supabase
    .from("inventory_lots")
    .select("id, inventory_id, quantity, expiry_date, received_at")
    .eq("inventory_id", inventoryRow.id)
    .gt("quantity", 0)
    .or(`expiry_date.is.null,expiry_date.gte.${today}`)
    .order("expiry_date", { ascending: true, nullsFirst: false })
    .order("received_at", { ascending: true });

  if (error) {
    throw error;
  }

  if (!lotRows || !lotRows.length) {
    return [];
  }

  const totalAvailable = lotRows.reduce(
    (sum, lot) => sum + Number(lot.quantity || 0),
    0
  );

  if (totalAvailable < requestedQuantity) {
    throw new Error("Lots insuffisants ou expires pour couvrir cette vente.");
  }

  let remaining = requestedQuantity;
  const consumedLots = [];

  for (const lot of lotRows) {
    if (remaining <= 0) {
      break;
    }
    const available = Number(lot.quantity || 0);
    if (available <= 0) {
      continue;
    }

    const consumed = Math.min(available, remaining);
    const nextLotQuantity = Number((available - consumed).toFixed(3));
    const { error: updateError } = await supabase
      .from("inventory_lots")
      .update({ quantity: nextLotQuantity })
      .eq("id", lot.id);

    if (updateError) {
      throw updateError;
    }

    await recordInventoryMovement({
      inventoryId: inventoryRow.id,
      lotId: lot.id,
      productId,
      movementType: "sale",
      quantity: consumed,
      unitType,
      referenceType: "order",
      referenceId,
      reason,
    });

    consumedLots.push({
      lotId: lot.id,
      quantity: consumed,
    });
    remaining = Number((remaining - consumed).toFixed(3));
  }

  if (remaining > 0) {
    throw new Error("Lots insuffisants ou expires pour couvrir cette vente.");
  }

  return consumedLots;
};

export const reintegrateCancelledOrderStock = async (previousOrder, nextOrder) => {
  if (!nextOrder?.stockReturned) {
    return false;
  }

  if (previousOrder?.stockReturned) {
    return false;
  }

  const orderId = nextOrder.id;
  const items = Array.isArray(nextOrder?.items) ? nextOrder.items : [];

  if (!orderId || !items.length) {
    return false;
  }

  const getItemReturnQuantity = (item) => {
    if (item?.unitType === "kg" || item?.soldWeightKg !== null) {
      return Number(item?.soldWeightKg ?? item?.quantity ?? 0);
    }
    return Number(item?.quantity || 0);
  };

  if (!isSupabaseConfigured || !supabase) {
    const cachedLots = readLotCache();
    const cachedMovements = readMovementCache();
    const nextLots = [...cachedLots];

    for (const item of items) {
      const returnQuantity = getItemReturnQuantity(item);
      if (returnQuantity <= 0) {
        continue;
      }

      const itemLotMovements = cachedMovements.filter(
        (movement) =>
          (movement.referenceId || movement.reference_id) === orderId &&
          (movement.productId || movement.product_id) === item.productId &&
          (movement.movementType || movement.movement_type) === "sale" &&
          (movement.lotId || movement.lot_id)
      );

      if (itemLotMovements.length) {
        itemLotMovements.forEach((movement) => {
          const lotId = movement.lotId || movement.lot_id;
          const quantity = Number(movement.quantity || 0);
          const lotIndex = nextLots.findIndex((lot) => lot.id === lotId);
          if (lotIndex >= 0) {
            nextLots[lotIndex] = {
              ...nextLots[lotIndex],
              quantity: Number((Number(nextLots[lotIndex].quantity || 0) + quantity).toFixed(3)),
            };
          }
        });
      } else {
        await adjustInventoryStock(
          item.productId,
          returnQuantity,
          `Reintegration apres annulation ${nextOrder.orderNumber || orderId}`
        );
      }
    }

    writeLotCache(nextLots);
    return true;
  }

  const { data: reintegrationMovements } = await supabase
    .from("inventory_movements")
    .select("id")
    .eq("reference_type", "order")
    .eq("reference_id", orderId)
    .eq("movement_type", "in");

  if (reintegrationMovements?.length) {
    return false;
  }

  const { data: saleMovements, error: saleMovementError } = await supabase
    .from("inventory_movements")
    .select("id, inventory_id, lot_id, product_id, quantity, unit_type")
    .eq("reference_type", "order")
    .eq("reference_id", orderId)
    .eq("movement_type", "sale");

  if (saleMovementError) {
    throw saleMovementError;
  }

  const saleMovementsByProduct = new Map();
  (saleMovements || []).forEach((movement) => {
    const productId = movement.product_id;
    const current = saleMovementsByProduct.get(productId) || [];
    current.push(movement);
    saleMovementsByProduct.set(productId, current);
  });

  for (const item of items) {
    const returnQuantity = getItemReturnQuantity(item);
    if (returnQuantity <= 0) {
      continue;
    }

    const inventoryRow = await getMainInventoryRow(item.productId);
    const nextStock = Number(inventoryRow.stock_quantity || 0) + returnQuantity;
    const { error: inventoryError } = await supabase
      .from("inventory")
      .update({ stock_quantity: nextStock })
      .eq("id", inventoryRow.id);

    if (inventoryError) {
      throw inventoryError;
    }

    const productSaleMovements = saleMovementsByProduct.get(item.productId) || [];

    if (productSaleMovements.length) {
      for (const movement of productSaleMovements) {
        const quantity = Number(movement.quantity || 0);

        if (movement.lot_id) {
          const { data: lotRow, error: lotFetchError } = await supabase
            .from("inventory_lots")
            .select("id, quantity")
            .eq("id", movement.lot_id)
            .maybeSingle();

          if (lotFetchError) {
            throw lotFetchError;
          }

          if (lotRow) {
            const { error: lotUpdateError } = await supabase
              .from("inventory_lots")
              .update({
                quantity: Number((Number(lotRow.quantity || 0) + quantity).toFixed(3)),
              })
              .eq("id", lotRow.id);

            if (lotUpdateError) {
              throw lotUpdateError;
            }
          }
        }

        await recordInventoryMovement({
          inventoryId: movement.inventory_id || inventoryRow.id,
          lotId: movement.lot_id || null,
          productId: item.productId,
          movementType: "in",
          quantity,
          unitType: movement.unit_type || item.unitType || "piece",
          referenceType: "order",
          referenceId: orderId,
          reason: `Reintegration apres annulation ${nextOrder.orderNumber || orderId}`,
        });
      }
    } else {
      await recordInventoryMovement({
        inventoryId: inventoryRow.id,
        productId: item.productId,
        movementType: "in",
        quantity: returnQuantity,
        unitType: item.unitType || "piece",
        referenceType: "order",
        referenceId: orderId,
        reason: `Reintegration apres annulation ${nextOrder.orderNumber || orderId}`,
      });
    }
  }

  return true;
};
